/** The performance report, with its own date range so any period can be read. */
import { Box, CircularProgress } from "@mui/material";
import { T } from "./tokens";
import { HsAdminGate } from "./HsAdminGate";
import PerformanceTab from "./PerformanceTab";
import MetricsTab from "./MetricsTab";
import { ReportRangeBar } from "./reportRange";
import type { ReportRange } from "./reportRange";
import type { WeeklyVisitSummary } from "./data";

const TABS = [
    { key: "performance", label: "Performance" },
    { key: "metrics", label: "Metrics" },
] as const;

export type ReportsTab = (typeof TABS)[number]["key"];

const ReportsScreen = ({
    data,
    isLoading,
    isError,
    team = "AllTeams",
    consultant = "AllConsultants",
    range,
    onRange,
    tab,
    onTab,
    onOpenConsultant,
}: {
    /** Range and tab live in the console so they survive opening a consultant and coming back. */
    range: ReportRange;
    onRange: (r: ReportRange) => void;
    tab: ReportsTab;
    onTab: (t: ReportsTab) => void;
    onOpenConsultant?: (email: string, name: string, teamName: string) => void;
    /** Only used as the access check: the weekly summary is returned to H&S admins only. */
    data: WeeklyVisitSummary | null | undefined;
    isLoading: boolean;
    isError: boolean;
    team?: string;
    consultant?: string;
}) => {

    if (isLoading)
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: "48px" }}>
                <CircularProgress size={26} />
            </Box>
        );
    if (isError) return <Box sx={{ p: "28px", color: T.muted }}>Couldn't load the report data.</Box>;
    if (!data) return <HsAdminGate />;

    return (
        <Box sx={{ padding: "24px 28px", display: "flex", flexDirection: "column", gap: "18px" }}>
            <Box sx={{ display: "flex", borderBottom: `1px solid ${T.border}` }}>
                {TABS.map((t) => (
                    <Box
                        key={t.key}
                        component="button"
                        onClick={() => onTab(t.key)}
                        sx={{
                            border: "none",
                            borderBottom: `2px solid ${tab === t.key ? T.purple : "transparent"}`,
                            background: "transparent",
                            color: tab === t.key ? T.ink2 : T.muted,
                            padding: "9px 16px",
                            fontSize: "13px",
                            fontWeight: 800,
                            cursor: "pointer",
                            fontFamily: T.font,
                        }}
                    >
                        {t.label}
                    </Box>
                ))}
            </Box>

            <ReportRangeBar range={range} onChange={onRange} />

            {tab === "performance" && <PerformanceTab range={range} team={team} consultant={consultant} onOpen={onOpenConsultant} />}
            {tab === "metrics" && <MetricsTab range={range} team={team} consultant={consultant} onOpen={onOpenConsultant} />}
        </Box>
    );
};

export default ReportsScreen;
